import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvGet, dvCreate, dvUpdate, dvDelete, dvClearLookup } from '../api/dataverseClient';

// Unified tagging across two separate pools: Task tags (dagrun_pm_tag /
// dagrun_pm_tasktag) and Decision tags (dagrun_pm_decisiontag /
// dagrun_pm_decisiontaglink). One interaction pattern, two data pools —
// the pools are never merged.

export const TagScope = {
  Project: 100000000,
  Tenant:  100000001,
} as const;

export interface Tag {
  id: string;
  name: string;
  scope: number | null;
  useCount: number;
  projectId: string | null;
  projectName: string | null;
}

export interface TagLink {
  linkId: string;
  tag: Tag;
}

// Everything pool-specific lives here so the service functions and the
// UI components are identical for both pools.
export interface TagPool {
  key: 'task' | 'decision';
  tagSet: string;          // tag entity set
  tagIdField: string;      // tag PK logical name
  linkSet: string;         // junction entity set
  linkIdField: string;     // junction PK logical name
  linkTagLookup: string;   // junction lookup → tag (also its nav property)
  linkEntityLookup: string; // junction lookup → parent entity
  entitySet: string;       // parent entity set (for @odata.bind)
}

export const TASK_TAG_POOL: TagPool = {
  key: 'task',
  tagSet: 'dagrun_pm_tags',
  tagIdField: 'dagrun_pm_tagid',
  linkSet: 'dagrun_pm_tasktags',
  linkIdField: 'dagrun_pm_tasktagid',
  linkTagLookup: 'dagrun_pm_tagid',
  linkEntityLookup: 'dagrun_pm_taskid',
  entitySet: 'dagrun_pm_tasks',
};

export const DECISION_TAG_POOL: TagPool = {
  key: 'decision',
  tagSet: 'dagrun_pm_decisiontags',
  tagIdField: 'dagrun_pm_decisiontagid',
  linkSet: 'dagrun_pm_decisiontaglinks',
  linkIdField: 'dagrun_pm_decisiontaglinkid',
  linkTagLookup: 'dagrun_pm_decisiontagid',
  linkEntityLookup: 'dagrun_pm_decisionid',
  entitySet: 'dagrun_pm_decisions',
};

type RawRow = Record<string, unknown>;

function toTag(pool: TagPool, raw: RawRow): Tag {
  const proj = raw.dagrun_pm_projectid as RawRow | undefined; // present only when expanded
  return {
    id: raw[pool.tagIdField] as string,
    name: (raw.dagrun_pm_name as string) ?? '',
    scope: (raw.dagrun_pm_scope as number | null) ?? null,
    useCount: (raw.dagrun_pm_usecount as number | null) ?? 0,
    projectId: (raw._dagrun_pm_projectid_value as string | null) ?? null,
    projectName: (proj?.dagrun_pm_name as string | null) ?? null,
  };
}

const TAG_SELECT = (pool: TagPool) =>
  [pool.tagIdField, 'dagrun_pm_name', 'dagrun_pm_scope', 'dagrun_pm_usecount', '_dagrun_pm_projectid_value'];

/**
 * All tags visible to a project: Tenant-scoped tags plus this project's
 * Project-scoped tags. This is the autocomplete source. Matching against
 * the result must be done case-insensitively in application code —
 * Dataverse has no case-insensitive-unique constraint on dagrun_pm_name.
 */
export async function getScopedTags(
  pca: IPublicClientApplication,
  pool: TagPool,
  projectId: string
): Promise<Tag[]> {
  const rows = await dvList<RawRow>(pca, pool.tagSet, {
    select: TAG_SELECT(pool),
    filter: `dagrun_pm_scope eq ${TagScope.Tenant} or _dagrun_pm_projectid_value eq ${projectId}`,
    orderby: 'dagrun_pm_name asc',
  });
  return rows.map(r => toTag(pool, r));
}

/** All tags in the pool, with owning-project names — Tenant Admin Panel. */
export async function getAllTags(
  pca: IPublicClientApplication,
  pool: TagPool
): Promise<Tag[]> {
  const rows = await dvList<RawRow>(pca, pool.tagSet, {
    select: TAG_SELECT(pool),
    expand: 'dagrun_pm_projectid($select=dagrun_pm_name)',
    orderby: 'dagrun_pm_usecount desc',
  });
  return rows.map(r => toTag(pool, r));
}

/** Tags currently applied to one task / decision. */
export async function getEntityTagLinks(
  pca: IPublicClientApplication,
  pool: TagPool,
  entityId: string
): Promise<TagLink[]> {
  const rows = await dvList<RawRow>(pca, pool.linkSet, {
    select: [pool.linkIdField],
    filter: `_${pool.linkEntityLookup}_value eq ${entityId}`,
    expand: `${pool.linkTagLookup}($select=${TAG_SELECT(pool).join(',')})`,
  });
  return rows
    .filter(r => r[pool.linkTagLookup])
    .map(r => ({
      linkId: r[pool.linkIdField] as string,
      tag: toTag(pool, r[pool.linkTagLookup] as RawRow),
    }));
}

/**
 * Every tag link in a project (filtered through the parent entity's project
 * lookup), keyed by parent entity id. Powers list-view pills + filters and
 * the Project Settings "tenant tags in use here" list.
 */
export async function getProjectTagLinks(
  pca: IPublicClientApplication,
  pool: TagPool,
  projectId: string
): Promise<Map<string, TagLink[]>> {
  const rows = await dvList<RawRow>(pca, pool.linkSet, {
    select: [pool.linkIdField, `_${pool.linkEntityLookup}_value`],
    filter: `${pool.linkEntityLookup}/_dagrun_pm_projectid_value eq ${projectId}`,
    expand: `${pool.linkTagLookup}($select=${TAG_SELECT(pool).join(',')})`,
  });
  const map = new Map<string, TagLink[]>();
  for (const r of rows) {
    if (!r[pool.linkTagLookup]) continue;
    const entityId = r[`_${pool.linkEntityLookup}_value`] as string;
    const link: TagLink = {
      linkId: r[pool.linkIdField] as string,
      tag: toTag(pool, r[pool.linkTagLookup] as RawRow),
    };
    const existing = map.get(entityId);
    if (existing) existing.push(link); else map.set(entityId, [link]);
  }
  return map;
}

/**
 * New tags are always Project-scoped to the current project. Promotion to
 * Tenant scope is an explicit admin action, never implicit at creation.
 */
export async function createTag(
  pca: IPublicClientApplication,
  pool: TagPool,
  projectId: string,
  name: string
): Promise<Tag> {
  const trimmed = name.trim();
  const id = await dvCreate(pca, pool.tagSet, {
    dagrun_pm_name: trimmed,
    dagrun_pm_scope: TagScope.Project,
    dagrun_pm_usecount: 0,
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
  });
  return { id, name: trimmed, scope: TagScope.Project, useCount: 0, projectId, projectName: null };
}

/**
 * Tenant-scoped tag creation — Tenant Admin Panel only. No project lookup
 * is set; the tag is immediately available in every project.
 */
export async function createTenantTag(
  pca: IPublicClientApplication,
  pool: TagPool,
  name: string
): Promise<Tag> {
  const trimmed = name.trim();
  const id = await dvCreate(pca, pool.tagSet, {
    dagrun_pm_name: trimmed,
    dagrun_pm_scope: TagScope.Tenant,
    dagrun_pm_usecount: 0,
  });
  return { id, name: trimmed, scope: TagScope.Tenant, useCount: 0, projectId: null, projectName: null };
}

/**
 * Links a tag to a task/decision and increments the tag's use count.
 * Use count tracks total historical usage (admin promotion signal) — it is
 * intentionally NOT decremented on removal.
 */
export async function attachTag(
  pca: IPublicClientApplication,
  pool: TagPool,
  entityId: string,
  entityName: string,
  tag: Tag
): Promise<TagLink> {
  const linkId = await dvCreate(pca, pool.linkSet, {
    dagrun_pm_name: `${tag.name} — ${entityName}`.slice(0, 200),
    [`${pool.linkTagLookup}@odata.bind`]: `/${pool.tagSet}(${tag.id})`,
    [`${pool.linkEntityLookup}@odata.bind`]: `/${pool.entitySet}(${entityId})`,
  });
  const newCount = (tag.useCount ?? 0) + 1;
  await dvUpdate(pca, pool.tagSet, tag.id, { dagrun_pm_usecount: newCount });
  return { linkId, tag: { ...tag, useCount: newCount } };
}

/**
 * Commits staged tags after the parent entity is created (New Task / Log
 * Decision modals). One junction per tag, each incrementing use count.
 * Sequential and best-effort per tag: the entity already exists, so one
 * failed junction shouldn't abort the rest.
 */
export async function attachTags(
  pca: IPublicClientApplication,
  pool: TagPool,
  entityId: string,
  entityName: string,
  tags: Tag[]
): Promise<void> {
  for (const tag of tags) {
    try {
      await attachTag(pca, pool, entityId, entityName, tag);
    } catch (e) {
      console.warn(`attachTags: failed to attach "${tag.name}"`, e);
    }
  }
}

/** Removes the junction record only. Use count is not decremented. */
export async function detachTag(
  pca: IPublicClientApplication,
  pool: TagPool,
  linkId: string
): Promise<void> {
  await dvDelete(pca, pool.linkSet, linkId);
}

/**
 * Promote a Project-scoped tag to Tenant scope: scope flips and the project
 * lookup is cleared. Junction records are untouched.
 */
export async function promoteTagToTenant(
  pca: IPublicClientApplication,
  pool: TagPool,
  tagId: string
): Promise<void> {
  await dvUpdate(pca, pool.tagSet, tagId, { dagrun_pm_scope: TagScope.Tenant });
  await dvClearLookup(pca, pool.tagSet, tagId, 'dagrun_pm_projectid');
}

/** Re-fetch a single tag — used to confirm promote/rename round-trips. */
export async function getTag(
  pca: IPublicClientApplication,
  pool: TagPool,
  tagId: string
): Promise<Tag> {
  const raw = await dvGet<RawRow>(pca, pool.tagSet, tagId, { select: TAG_SELECT(pool) });
  return toTag(pool, raw);
}

/**
 * Rename in place. Tasks/decisions reference the tag through the junction
 * lookup, so the new name shows up everywhere without touching any junction
 * or parent record.
 */
export async function renameTag(
  pca: IPublicClientApplication,
  pool: TagPool,
  tagId: string,
  newName: string
): Promise<void> {
  await dvUpdate(pca, pool.tagSet, tagId, { dagrun_pm_name: newName.trim() });
}

/** How many tasks/decisions currently reference this tag via the junction. */
export async function countTagUsage(
  pca: IPublicClientApplication,
  pool: TagPool,
  tagId: string
): Promise<number> {
  const rows = await dvList<RawRow>(pca, pool.linkSet, {
    select: [pool.linkIdField],
    filter: `_${pool.linkTagLookup}_value eq ${tagId}`,
  });
  return rows.length;
}

/**
 * Deletes a tag and every junction row referencing it, so it disappears
 * from every task/decision it was applied to with no orphaned junctions.
 * Shared by Project Settings (Project-scoped tags) and the Tenant Admin
 * Panel (any scope). Junctions go first — the tag record last — so a
 * partial failure never leaves links pointing at a deleted tag.
 */
export async function deleteTag(
  pca: IPublicClientApplication,
  pool: TagPool,
  tagId: string
): Promise<void> {
  const rows = await dvList<RawRow>(pca, pool.linkSet, {
    select: [pool.linkIdField],
    filter: `_${pool.linkTagLookup}_value eq ${tagId}`,
  });
  await Promise.all(rows.map(r => dvDelete(pca, pool.linkSet, r[pool.linkIdField] as string)));
  await dvDelete(pca, pool.tagSet, tagId);
}

/** Case-insensitive exact-name lookup — the app-level dedupe primitive. */
export function findTagByName(tags: Tag[], name: string): Tag | undefined {
  const target = name.trim().toLowerCase();
  return tags.find(t => t.name.trim().toLowerCase() === target);
}
